import { computed, DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ShoppingCartService } from '@core/services/shopping-cart/shopping-cart.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from '@interfaces/api/api.interface';
import { ShoppingCartGetItemsResponseInterface } from '@interfaces/shopping-cart/shopping-cart-get-items-response-interface';
import { ShoppingCartAddItemInterface } from '@interfaces/shopping-cart/shopping-cart-add-item-interface';
import { catchError, map, Observable, of, tap, timer } from 'rxjs';
import { ShoppingCartItemsInterface } from '@interfaces/shopping-cart/shopping-cart-items-interface';
import { AmountInterface } from '@interfaces/shopping-cart/amount-interface';

@Injectable({ providedIn: 'root' })
export class ShoppingCartState {
  private TAG = 'ShoppingCartState';
  private _shoppingCartItems: WritableSignal<ShoppingCartItemsInterface[]> = signal([]);
  private _shoppingCartItemsCount: WritableSignal<number> = signal(0);
  private _shoppingCartResumeSubtotal: WritableSignal<number> = signal(0);
  private shoppingCartService = inject(ShoppingCartService);
  private destroyRef = inject(DestroyRef);
  shoppingCartItemsCount = computed(() => this._shoppingCartItemsCount());
  shoppingCartResumeSubtotal = computed(() => this._shoppingCartResumeSubtotal());
  shoppingCartItems = computed(() => this._shoppingCartItems());
  private _productDeletedFromCart: WritableSignal<boolean> = signal(false);
  private _existSinglePieceProduct: WritableSignal<boolean> = signal(false);
  private _hydrated: WritableSignal<boolean> = signal(false);
  existSinglePieceProduct = computed(() => this._existSinglePieceProduct());
  productDeletedFromCart = computed(() => this._productDeletedFromCart());
  constructor() {
    this.getShoppingCartItems();
  }

  getShoppingCartItemsCount(): number {
    return this._shoppingCartItems().reduce((sum, item) => {
      return sum + item.cantidad;
    }, 0);
  }
  getShoppingCartItems(): void {
    this.shoppingCartService
      .getShoppingCartItems()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<ShoppingCartGetItemsResponseInterface>) => {
          if (res.success) {
            this._productDeletedFromCart.set(false);
            const shoppingCartItems = res.data;
            this._shoppingCartItems.set(shoppingCartItems.items);
            this._hydrated.set(true);
            this._shoppingCartItemsCount.set(this.getShoppingCartItemsCount());
            this._shoppingCartResumeSubtotal.set(shoppingCartItems.precioVentaTotal);
            this._existSinglePieceProduct.set(this.checkForSinglePieceProduct());
          }
        },
        error: err => {
          this._productDeletedFromCart.set(false);
          this._hydrated.set(false);
          this._shoppingCartItemsCount.set(0);
          this._existSinglePieceProduct.set(false);
          console.log(`${this.TAG} ${err}`);
        },
      });
  }

  private checkForSinglePieceProduct(): boolean {
    const result = this._shoppingCartItems().find(
      shoppingCartItem => shoppingCartItem.producto.productoPrincipal.isPiezaUnica
    );
    return result != undefined;
  }

  addToCart(item: ShoppingCartAddItemInterface): Observable<void> {
    const productList: ShoppingCartAddItemInterface[] = [item];
    return this.shoppingCartService.createShoppingCartItem(productList).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((res: ApiResponse<void>) => {
        if (res.success) {
          this.getShoppingCartItems();
        }
      }),
      catchError(err => {
        console.log(`${this.TAG} ${err}`);
        return of(void 0);
      }),
      map(() => void 0)
    );
  }

  deleteItem(itemID: string): Observable<void> {
    return this.shoppingCartService.deleteShoppingCartItem(itemID).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((res: ApiResponse<void>) => {
        if (res.success) {
          this._productDeletedFromCart.set(true);
          timer(1000).subscribe(() => {
            this.getShoppingCartItems();
          });
        }
      }),
      catchError(err => {
        console.log(`${this.TAG} ${err}`);
        return of(void 0);
      }),
      map(() => void 0)
    );
  }

  updateItem(itemID: string, amount: AmountInterface): Observable<void> {
    return this.shoppingCartService.updateShoppingCartItem(itemID, amount).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((res: ApiResponse<void>) => {
        if (res.success) {
          this.getShoppingCartItems();
        }
      }),
      catchError(err => {
        console.log(`${this.TAG} ${err}`);
        return of(void 0);
      }),
      map(() => void 0)
    );
  }
}
